import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useT } from '@/app/i18n/client';
import type { BookedSlot } from '@/entities/room/types';
import { SLOT_QUERY_KEYS } from '@/entities/slot/keys';
import apiGetSlot from '@/features/slot/apis/get_slot';
import apiGetSlotBoxList from '@/features/slot/apis/get_slot_box_list';
import apiGetSlotDateList from '@/features/slot/apis/get_slot_date_list';
import useSlotSelectOption from '@/features/slot/hooks/useSelectOption';
import { utilDateGetMonth, utilDateThisMonth } from '@/lib/date';
import { useProductStore } from '@/stores/product';

import { useShowCalendarContext } from '../contexts/ShowCalendarStoreContext';
import useProductSelectOptionGroup from './useSelectOptionGroup';

export const useSlotState = (idx: string, isExpanded: boolean) => {
  const { t, i18n } = useT('product');

  const setBookedSlots = useProductStore(state => state.setBookedSlots);
  const { showCalendarButtonRef, showCalendar, toggleCalendar } = useShowCalendarContext();

  // Calendar states
  const [currentMonth, setCurrentMonth] = useState<Date>(utilDateThisMonth());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Loading state for delayed loading indicator (0.5 seconds)
  const [showSlotBoxListLoading, setShowSlotBoxListLoading] = useState<boolean>(false);

  // Slot Option
  const {
    slotOptionQuantity,
    selectedSlotOptionList,
    handleUpdateSlotOptionQuantity,
    handleSelectSlotOption,
    handleResetSlotOption,
  } = useSlotSelectOption(t, isExpanded);

  const { data: slotDateList } = useQuery({
    queryKey: [SLOT_QUERY_KEYS.GET_SLOT_DATE_LIST, idx],
    queryFn: async () => {
      const response = await apiGetSlotDateList(idx);

      // response의 startDate를 월 첫 날로 설정
      if (response?.[0]?.date) {
        setCurrentMonth(utilDateGetMonth(response[0].date));
      }

      return response;
    },
    throwOnError: true,
    retry: false,
  });

  const { data: slotData } = useQuery({
    queryKey: [SLOT_QUERY_KEYS.GET_SLOT, idx, i18n.language, selectedDate],
    queryFn: () => apiGetSlot({ idx, lng: i18n.language, date: selectedDate }),
    enabled: isExpanded,
    throwOnError: true,
    retry: false,
    placeholderData: keepPreviousData,
  });

  // Option
  const { selectedOptionGroupList, handleSelectOption, handleResetOptionGroup } =
    useProductSelectOptionGroup(slotData?.optionGroupList, isExpanded);

  const { data: slotBoxList } = useQuery({
    queryKey: [SLOT_QUERY_KEYS.GET_SLOT_BOX_LIST, idx, i18n.language, selectedDate],
    queryFn: async () => {
      setShowSlotBoxListLoading(true);
      handleResetSlotOption();
      handleResetOptionGroup();

      const slotBoxList = await apiGetSlotBoxList({ idx, lng: i18n.language, date: selectedDate });
      setShowSlotBoxListLoading(false);
      return slotBoxList;
    },
    enabled: isExpanded,
    throwOnError: true,
    retry: false,
    gcTime: 0,
    placeholderData: keepPreviousData,
  });

  const handleChangeMonth =
    (newMonth: Date) =>
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();

      setCurrentMonth(newMonth);
    };

  const handleDateClick = (selectedDateParam: Date): void => {
    const clickedDate = new Date(
      selectedDateParam.getFullYear(),
      selectedDateParam.getMonth(),
      selectedDateParam.getDate(),
    );

    setSelectedDate(clickedDate);
  };

  const addSlot = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();

    // 날짜 체크
    if (!selectedDate) {
      toast.error(t('messages.selectDate'));
      if (!showCalendar) toggleCalendar();
      showCalendarButtonRef?.current?.focus();
      return;
    }

    // 필수 옵션 체크
    const requiredOptionGroupList = slotData?.optionGroupList.filter(
      optionGroup => optionGroup.isRequired,
    );
    if (requiredOptionGroupList && requiredOptionGroupList.length > 0) {
      // 필수 옵션 그룹 중 하나라도 선택되지 않은 경우 에러 메시지 표시
      const unselectedRequiredGroup = requiredOptionGroupList.find(requiredGroup => {
        const selectedGroup = selectedOptionGroupList.find(
          selectedGroup => selectedGroup.optionGroupIdx === requiredGroup.idx,
        );
        return !selectedGroup || selectedGroup.optionList.length === 0;
      });

      if (unselectedRequiredGroup) {
        toast.error(
          t('messages.selectRequiredOption', { optionGroupTitle: unselectedRequiredGroup.title }),
        );
        return;
      }
    }

    // 슬롯 옵션 혹은 옵션 선택 체크
    if (selectedSlotOptionList.length === 0) {
      toast.error(t('messages.selectItem'));
      return;
    }
    // 슬롯 옵션 수량 체크
    if (selectedSlotOptionList.length > 0 && slotOptionQuantity === 0) {
      toast.error(t('messages.selectQuantity'));
      return;
    }

    // 옵션 그룹 선택된 것만 필터링
    const filteredSelectedOptionGroupList = selectedOptionGroupList.filter(
      optionGroup => optionGroup.optionList.length > 0,
    );

    const newSlot: BookedSlot = {
      idx,
      title: slotData?.title || '',
      date: selectedDate,
      slotOptionTitle: slotData?.slotOptionTitle || '',
      slotOptionQuantity,
      slotOptionList: selectedSlotOptionList,
      optionGroupList: filteredSelectedOptionGroupList,
      totalAmount,
    };

    setBookedSlots(prev => [...prev, newSlot]);

    handleResetOptionGroup();
    handleResetSlotOption();

    toast.success(t('success.add', { title: slotData?.title || t('slot') }));
  };

  const totalAmount = useMemo((): number => {
    const slotOptionPrice =
      selectedSlotOptionList.length > 0 ? selectedSlotOptionList[0].price * slotOptionQuantity : 0;
    const optionPrice =
      selectedOptionGroupList.reduce(
        (sum, option) => sum + option.optionList.reduce((sum, option) => sum + option.price, 0),
        0,
      ) * slotOptionQuantity;
    return slotOptionPrice + optionPrice;
  }, [selectedOptionGroupList, selectedSlotOptionList, slotOptionQuantity]);

  return {
    t,
    slotData,
    slotDateList,
    slotBoxList,
    showSlotBoxListLoading,

    // Calendar states
    currentMonth,
    selectedDate,
    handleDateClick,
    handleChangeMonth,

    // Slot Option
    slotOptionQuantity,
    handleUpdateSlotOptionQuantity,
    selectedSlotOptionList,
    handleSelectSlotOption,

    // Option
    selectedOptionGroupList,
    handleSelectOption,

    // Total
    totalAmount,

    // Add Slot
    addSlot,
  };
};
